import React, { useState, useCallback, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Animated, Image } from 'react-native';
import { IActivity, QuizActivityConfig, QuizQuestion, KeyStage } from '@eduwrite/shared';
import { StageCard, JuicyButton } from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { useStrokeStore } from '../store/useStrokeStore';
import { useAccessibilityStore } from '../store/useAccessibilityStore';
import { Sparkles, Check, Volume2, Star } from 'lucide-react-native';

interface PictorialChoiceActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const PictorialChoiceActivity: React.FC<PictorialChoiceActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as QuizActivityConfig;
    const questions: QuizQuestion[] = config.questions || [];

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [completed, setCompleted] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const highContrast = useAccessibilityStore((s) => s.highContrast);
    const bounceAnim = useRef(new Animated.Value(1)).current;
    const heroPulseAnim = useRef(new Animated.Value(1)).current;

    const currentQuestion = questions[currentIndex] || null;
    const isLastQuestion = currentIndex >= questions.length - 1;

    // Pulse the hero icon gently on question change
    useEffect(() => {
        heroPulseAnim.setValue(0.9);
        Animated.spring(heroPulseAnim, {
            toValue: 1,
            friction: 4,
            tension: 50,
            useNativeDriver: true,
        }).start();
        setSelectedOptionId(answers[currentQuestion?.id || ''] || null);
        setFeedbackMessage(null);
    }, [currentIndex, currentQuestion?.id]);

    const handleSelectOption = useCallback((optionId: string) => {
        setSelectedOptionId(optionId);
        setFeedbackMessage(null);

        // Bouncy feedback on tile press
        Animated.sequence([
            Animated.timing(bounceAnim, { toValue: 1.05, duration: 100, useNativeDriver: true }),
            Animated.timing(bounceAnim, { toValue: 1, duration: 120, useNativeDriver: true }),
        ]).start();
    }, [bounceAnim]);

    const handleConfirmAnswer = useCallback(async () => {
        if (!currentQuestion || !selectedOptionId || isSubmitting) return;

        setIsSubmitting(true);
        const nextAnswers = { ...answers, [currentQuestion.id]: selectedOptionId };
        setAnswers(nextAnswers);

        const isOptionCorrect = selectedOptionId === currentQuestion.correctOptionId;
        const correctCount = questions.filter(
            (q) => (q.id === currentQuestion.id ? selectedOptionId : answers[q.id]) === q.correctOptionId
        ).length;

        // Visual feedback banner
        setFeedbackMessage(isOptionCorrect ? '🌟 Brilliant! Good job!' : '👍 Nice try! Keep going!');

        try {
            await StrokeSyncService.sendQuizAnswer({
                activityId: activity.id,
                questionId: currentQuestion.id,
                selectedOptionId,
                questionIndex: currentIndex,
                totalQuestions: questions.length,
                score: correctCount,
            });
        } catch {
            // Non-blocking sync error
        }

        // Store structured evaluation
        useStrokeStore.getState().setStructuredData({
            answers: Object.entries(nextAnswers).map(([qId, optId]) => ({
                questionId: qId,
                selectedOptionId: optId,
                isCorrect: questions.find((q) => q.id === qId)?.correctOptionId === optId,
            })),
            score: Math.round((correctCount / questions.length) * 100),
            totalQuestions: questions.length,
            isCorrect: correctCount === questions.length,
            inputModeUsed: 'choice_tap',
        });

        // Delay advance for celebratory feedback
        setTimeout(() => {
            setIsSubmitting(false);
            if (currentIndex < questions.length - 1) {
                setCurrentIndex((prev) => prev + 1);
            } else {
                setCompleted(true);
            }
        }, 1100);
    }, [currentQuestion, selectedOptionId, isSubmitting, answers, questions, activity.id, currentIndex]);

    if (completed) {
        const correctCount = questions.filter((q) => answers[q.id] === q.correctOptionId).length;
        return (
            <ScrollView contentContainerStyle={styles.scrollContainer} testID="pictorial-choice-activity">
                <StageCard style={styles.completionCard} testID="completion-card">
                    <View style={styles.trophyWrap}>
                        <Star size={72} color="#F59E0B" fill="#F59E0B" />
                    </View>
                    <Text style={styles.completionTitle} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        Super Star! 🌟
                    </Text>
                    <Text style={styles.completionSubtitle} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                        You finished all {questions.length} questions!
                    </Text>
                    <View style={styles.starsSummary}>
                        <Text style={styles.starsScoreText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                            {correctCount} out of {questions.length} right!
                        </Text>
                    </View>
                    <View style={styles.finishBtnWrap}>
                        <JuicyButton
                            label="Finish"
                            variant="primary"
                            size="large"
                            onPress={onComplete}
                            testID="quiz-finish-button"
                        />
                    </View>
                </StageCard>
            </ScrollView>
        );
    }

    if (!currentQuestion) {
        return (
            <View style={styles.emptyContainer} testID="pictorial-choice-activity">
                <StageCard style={styles.stageCard}>
                    <Text style={styles.emptyText}>No questions ready.</Text>
                    <JuicyButton label="Finish" variant="secondary" size="medium" onPress={onComplete} />
                </StageCard>
            </View>
        );
    }

    const heroIcon = currentQuestion.heroIcon || '🌟';
    const spokenPrompt = currentQuestion.audioPromptText || currentQuestion.text;

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.scrollContainer}
            testID="pictorial-choice-activity"
            showsVerticalScrollIndicator={true}
            bounces={false}
        >
            <StageCard style={styles.stageCard} testID="pictorial-stage-card">
                {/* Progress Indicators & Step Dots */}
                <View style={styles.progressRow}>
                    <View style={styles.dotsWrap}>
                        {questions.map((q, idx) => {
                            const isCurrent = idx === currentIndex;
                            const isDone = !!answers[q.id];
                            return (
                                <View
                                    key={q.id}
                                    style={[
                                        styles.progressDot,
                                        isDone && styles.progressDotDone,
                                        isCurrent && styles.progressDotCurrent,
                                    ]}
                                />
                            );
                        })}
                    </View>
                    <Text style={styles.progressText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                        {currentIndex + 1} of {questions.length}
                    </Text>
                </View>

                {/* Spoken Audio & Question Header */}
                <View style={styles.promptHeader}>
                    <View style={styles.audioWrap}>
                        <AudioPromptButton
                            text={spokenPrompt}
                            audioUrl={currentQuestion.audioPromptUrl}
                            autoPlay={true}
                            testID="audio-prompt-button"
                        />
                    </View>
                    <Text
                        style={[
                            styles.questionPromptText,
                            highContrast && styles.highContrastText,
                        ]}
                        allowFontScaling={true}
                        maxFontSizeMultiplier={1.3}
                        testID="question-prompt-text"
                    >
                        {currentQuestion.text}
                    </Text>
                </View>

                {/* Center Hero Stimulus Island */}
                <Animated.View style={[styles.heroIsland, { transform: [{ scale: heroPulseAnim }] }]}>
                    {currentQuestion.heroImage ? (
                        <Image
                            source={{ uri: currentQuestion.heroImage }}
                            style={styles.heroImage}
                            resizeMode="contain"
                            accessibilityLabel="Visual question stimulus"
                        />
                    ) : (
                        <View style={styles.heroIconBadge}>
                            <Text style={styles.heroIconEmoji}>{heroIcon}</Text>
                        </View>
                    )}
                </Animated.View>

                {/* Large Tactile Visual Option Cards (Zero dead space) */}
                <View style={styles.optionsContainer}>
                    {currentQuestion.options.map((option) => {
                        const isSelected = selectedOptionId === option.id;
                        const optionIcon = option.icon || (option.text.match(/\p{Extended_Pictographic}/u)?.[0] ?? '✨');
                        const cleanLabel = option.text.replace(/\p{Extended_Pictographic}/gu, '').trim();

                        return (
                            <TouchableOpacity
                                key={option.id}
                                testID={`pictorial-option-${option.id}`}
                                style={[
                                    styles.optionCard,
                                    isSelected && styles.optionCardSelected,
                                    highContrast && isSelected && styles.optionCardSelectedHighContrast,
                                ]}
                                onPress={() => handleSelectOption(option.id)}
                                activeOpacity={0.8}
                                accessibilityRole="radio"
                                accessibilityState={{ selected: isSelected }}
                                accessibilityLabel={option.text}
                            >
                                {isSelected && (
                                    <View style={styles.selectedBadge}>
                                        <Check size={18} color="#FFFFFF" strokeWidth={3} />
                                    </View>
                                )}

                                {option.imageUrl ? (
                                    <Image
                                        source={{ uri: option.imageUrl }}
                                        style={styles.optionImage}
                                        resizeMode="contain"
                                    />
                                ) : (
                                    <View style={styles.optionIconCircle}>
                                        <Text style={styles.optionIconEmoji}>{optionIcon}</Text>
                                    </View>
                                )}

                                <Text
                                    style={[
                                        styles.optionLabel,
                                        isSelected && styles.optionLabelSelected,
                                        highContrast && styles.highContrastText,
                                    ]}
                                    allowFontScaling={true}
                                    maxFontSizeMultiplier={1.3}
                                    numberOfLines={2}
                                >
                                    {cleanLabel || option.text}
                                </Text>

                                {/* Pronunciation button on option card for pre-readers */}
                                <View style={styles.optionAudioPill}>
                                    <Volume2 size={16} color={isSelected ? '#15803D' : '#64748B'} />
                                    <Text style={[styles.optionAudioPillText, isSelected && styles.optionAudioPillTextSelected]}>
                                        Hear
                                    </Text>
                                </View>
                            </TouchableOpacity>
                        );
                    })}
                </View>

                {/* Feedback Banner on Selection */}
                {feedbackMessage && (
                    <View style={styles.feedbackBanner} testID="feedback-banner">
                        <Sparkles size={20} color="#15803D" />
                        <Text style={styles.feedbackText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                            {feedbackMessage}
                        </Text>
                    </View>
                )}

                {/* Bottom Hero Action: Apple Green JuicyButton "GO!" */}
                <View style={styles.actionWrap}>
                    <JuicyButton
                        label={isLastQuestion ? 'DONE!' : 'GO!'}
                        variant="primary"
                        size="large"
                        disabled={!selectedOptionId || isSubmitting}
                        onPress={handleConfirmAnswer}
                        testID="pictorial-confirm-button"
                        style={styles.confirmButton}
                    />
                </View>
            </StageCard>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    scrollContainer: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingHorizontal: 12,
        paddingVertical: 12,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        padding: 16,
    },
    emptyText: {
        fontSize: 18,
        fontWeight: '700',
        color: '#64748B',
        textAlign: 'center',
        marginBottom: 16,
    },
    stageCard: {
        borderRadius: 32,
        padding: 20,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.1,
        shadowRadius: 16,
        elevation: 6,
    },
    progressRow: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
        paddingHorizontal: 4,
    },
    dotsWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    progressDot: {
        width: 14,
        height: 14,
        borderRadius: 7,
        backgroundColor: '#E2E8F0',
    },
    progressDotCurrent: {
        width: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: '#3B82F6',
        borderWidth: 2,
        borderColor: '#93C5FD',
    },
    progressDotDone: {
        backgroundColor: '#22C55E',
    },
    progressText: {
        fontSize: 15,
        fontWeight: '800',
        color: '#64748B',
    },
    promptHeader: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        borderRadius: 20,
        paddingHorizontal: 14,
        paddingVertical: 12,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        marginBottom: 14,
        gap: 12,
    },
    audioWrap: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    questionPromptText: {
        flex: 1,
        fontSize: 20,
        fontWeight: '800',
        color: '#0F172A',
        lineHeight: 28,
    },
    highContrastText: {
        color: '#000000',
        fontWeight: '900',
    },
    heroIsland: {
        alignItems: 'center',
        justifyContent: 'center',
        marginVertical: 8,
    },
    heroIconBadge: {
        width: 96,
        height: 96,
        borderRadius: 48,
        backgroundColor: '#EFF6FF',
        borderWidth: 3,
        borderColor: '#BFDBFE',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#3B82F6',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 3,
    },
    heroIconEmoji: {
        fontSize: 52,
    },
    heroImage: {
        width: 160,
        height: 120,
        borderRadius: 16,
    },
    optionsContainer: {
        width: '100%',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 16,
        marginVertical: 12,
    },
    optionCard: {
        flex: 1,
        minWidth: 140,
        maxWidth: 220,
        minHeight: 140,
        backgroundColor: '#F8FAFC',
        borderRadius: 24,
        borderWidth: 2,
        borderColor: '#E2E8F0',
        borderBottomWidth: 6,
        borderBottomColor: '#CBD5E1',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 14,
        position: 'relative',
    },
    optionCardSelected: {
        backgroundColor: '#F0FDF4',
        borderColor: '#22C55E',
        borderBottomWidth: 6,
        borderBottomColor: '#16A34A',
        transform: [{ scale: 1.03 }],
    },
    optionCardSelectedHighContrast: {
        borderColor: '#000000',
        borderBottomColor: '#000000',
        backgroundColor: '#FEF08A',
    },
    selectedBadge: {
        position: 'absolute',
        top: 8,
        right: 8,
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: '#22C55E',
        alignItems: 'center',
        justifyContent: 'center',
    },
    optionIconCircle: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 8,
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 4,
        elevation: 2,
    },
    optionIconEmoji: {
        fontSize: 36,
    },
    optionImage: {
        width: 64,
        height: 64,
        marginBottom: 8,
    },
    optionLabel: {
        fontSize: 18,
        fontWeight: '800',
        color: '#1E293B',
        textAlign: 'center',
        marginTop: 2,
    },
    optionLabelSelected: {
        color: '#15803D',
        fontWeight: '900',
    },
    optionAudioPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: 8,
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    optionAudioPillText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#64748B',
    },
    optionAudioPillTextSelected: {
        color: '#15803D',
        fontWeight: '800',
    },
    feedbackBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: '#DCFCE7',
        borderColor: '#86EFAC',
        borderWidth: 1,
        borderRadius: 16,
        paddingHorizontal: 16,
        paddingVertical: 8,
        marginVertical: 8,
    },
    feedbackText: {
        fontSize: 16,
        fontWeight: '800',
        color: '#166534',
    },
    actionWrap: {
        width: '100%',
        alignItems: 'center',
        marginTop: 10,
    },
    confirmButton: {
        width: '100%',
        maxWidth: 280,
    },
    completionCard: {
        borderRadius: 32,
        padding: 32,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.1,
        shadowRadius: 16,
        elevation: 6,
    },
    trophyWrap: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: '#FEF3C7',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    completionTitle: {
        fontSize: 28,
        fontWeight: '900',
        color: '#0F172A',
        marginBottom: 6,
    },
    completionSubtitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#64748B',
        marginBottom: 16,
    },
    starsSummary: {
        backgroundColor: '#F1F5F9',
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 20,
        marginBottom: 24,
    },
    starsScoreText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#334155',
    },
    finishBtnWrap: {
        width: '100%',
        maxWidth: 240,
    },
});
